import React, { useState, useEffect } from 'react';
import { getSortedFavourites } from '../config/devices';

export function PageLayout({ children, onFullscreen, isFullscreen, selectedLocation, onFavouriteClick, favouritesRefreshTrigger, isConnectionScreen = false }) {
    const [favourites, setFavourites] = useState({ rail: [], bus: [] });

    // Load favourites when selectedLocation changes or when refresh is triggered
    useEffect(() => {
        if (selectedLocation) {
            const locationFavourites = getSortedFavourites(selectedLocation);
            setFavourites(locationFavourites);
        }
    }, [selectedLocation, favouritesRefreshTrigger]);

    const handleFavouriteClick = (favourite) => {
        if (onFavouriteClick && !isConnectionScreen) {
            onFavouriteClick(favourite);
        }
    };

    return (
        <div className="w-full h-full bg-journey-blue flex flex-col">
            {/* Portrait Layout - Original design */}
            <div className="portrait:flex flex-1 landscape:hidden">
                <div className="flex-1 bg-white rounded-tl-3xl rounded-tr-3xl rounded-bl-3xl rounded-br-3xl m-2 sm:m-4 relative overflow-hidden">
                    {children}

                    {/* Fullscreen Toggle Button - consistent position on all screens */}
                    {onFullscreen && (
                        <button
                            onClick={onFullscreen}
                            className="absolute bottom-4 left-4 w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-700 transition-all duration-200 hover:scale-110 z-50"
                            title={isFullscreen ? "Exit fullscreen (Esc)" : "Enter fullscreen"}
                        >
                            {isFullscreen ? (
                                // Exit fullscreen icon (compress)
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                                </svg>
                            ) : (
                                // Enter fullscreen icon (expand)
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                </svg>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Landscape Layout - With sidebar */}
            <div className="hidden landscape:flex h-full">
                {/* Left Sidebar - Favourites */}
                <div className="w-80 flex flex-col px-4 sm:px-8 pb-6 pt-6 sm:pt-12 relative">
                    <h2 className="text-base sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-6">Favourites</h2>
                    
                    {/* Rail Station Favourites */}
                    <div className="space-y-1 mb-6">
                        {favourites.rail.map((station, index) => (
                            <button
                                key={`rail-${station.stopCode}-${index}`}
                                onClick={() => handleFavouriteClick(station)}
                                disabled={isConnectionScreen}
                                className={`w-full flex items-center gap-3 text-left p-1 rounded-lg transition-colors ${
                                    isConnectionScreen 
                                        ? 'text-white text-opacity-40 cursor-not-allowed' 
                                        : 'text-white hover:bg-white hover:bg-opacity-10'
                                }`}
                            >
                                <i className={`zip-icon-mode_rail_stop text-lg ${
                                    isConnectionScreen ? 'text-white text-opacity-40' : 'text-white'
                                }`}></i>
                                <span className="text-base">{station.name}</span>
                            </button>
                        ))}
                    </div>

                    {/* Bus Stop Favourites */}
                    {favourites.bus.length > 0 && (
                        <div className="space-y-1">
                            {favourites.bus.map((stop, index) => (
                                <button
                                    key={`bus-${stop.stopCode || stop.geohash}-${index}`}
                                    onClick={() => handleFavouriteClick(stop)}
                                    disabled={isConnectionScreen}
                                    className={`w-full flex items-center gap-3 text-left p-1 rounded-lg transition-colors ${
                                        isConnectionScreen 
                                            ? 'text-white text-opacity-40 cursor-not-allowed' 
                                            : 'text-white hover:bg-white hover:bg-opacity-10'
                                    }`}
                                >
                                    <svg className={`w-5 h-5 ${
                                        isConnectionScreen ? 'text-white text-opacity-40' : 'text-white'
                                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                    </svg>
                                    <span className="text-base">{stop.name}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Fullscreen Toggle Button - floating at bottom of sidebar */}
                    {onFullscreen && (
                        <button
                            onClick={onFullscreen}
                            className="absolute bottom-4 left-4 w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-700 transition-all duration-200 hover:scale-110 z-10"
                            title={isFullscreen ? "Exit fullscreen (Esc)" : "Enter fullscreen"}
                        >
                            {isFullscreen ? (
                                // Exit fullscreen icon (compress)
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                                </svg>
                            ) : (
                                // Enter fullscreen icon (expand)
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                </svg>
                            )}
                        </button>
                    )}
                </div>

                {/* Main white content area */}
                <div className="flex-1 bg-white rounded-tl-3xl rounded-tr-3xl rounded-bl-3xl rounded-br-3xl m-2 ml-0 relative overflow-hidden">
                    {children}
                </div>
            </div>
        </div>
    );
}
